import { CartAddEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * BEKRA automatic free towel.
 *
 * Shopify's discounts are the source of truth for the price:
 *   - Buy X Get Y "Gratis Baumwoll-Handtuch ab 35 € Bestellwert" (original prices, towel excluded)
 *   - app volume discount "3 Artikel" (3 units of the SAME crystal product)
 * Optional bonus gift (Theme settings > bekra_gift_crystal_bonus_product, e.g. Reisedeo):
 * only for the crystal rule, priced 0 by One Stop Volume Discounts ("3/6 Artikel").
 * This script manages ONE gift line per gift (line item property `_bekra_free_gift`):
 *   - adds it when the cart qualifies,
 *   - verifies Shopify really prices it 0 - otherwise removes it again and remembers
 *     that cart state, so there is never a paid "gift" and never an add/remove loop,
 *   - removes it when the cart no longer qualifies,
 *   - never touches towel lines the customer added themselves.
 */

const SOURCE = 'bekra-free-gift';
const REJECTED_KEY = 'bekra-free-gift-rejected';

const configElement = document.getElementById('bekra-free-gift-config');
const config = configElement ? JSON.parse(configElement.textContent || '{}') : null;

let busy = false;
let queued = false;

/** @param {any} item */
const isGiftLine = (item) => item.properties && item.properties._bekra_free_gift;

/** @param {any} cart - one crystal product reaches the quantity (sizes combinable) */
function crystalRule(cart) {
  /** @type {Record<string, number>} */
  const crystalQty = {};
  for (const item of cart.items) {
    if (config.crystalProductIds.includes(item.product_id)) {
      crystalQty[item.product_id] = (crystalQty[item.product_id] || 0) + item.quantity;
    }
  }
  return Object.values(crystalQty).some((qty) => qty >= config.crystalQty);
}

/** @param {any} cart */
function qualifies(cart) {
  // Products Shopify already gives away in a "buy X get Y" deal (a line priced 0,
  // e.g. Kristall Deo Spray 2+1) never count towards the towel threshold.
  const freeDealIds = new Set(
    cart.items
      .filter(
        (item) =>
          !isGiftLine(item) &&
          item.product_id !== config.giftProductId &&
          item.final_line_price === 0 &&
          item.original_line_price > 0
      )
      .map((item) => item.product_id)
  );
  let subtotal = 0;
  for (const item of cart.items) {
    if (item.product_id === config.giftProductId || isGiftLine(item)) continue;
    if (!freeDealIds.has(item.product_id)) subtotal += item.original_line_price;
  }
  const byValue = config.thresholdCents > 0 && subtotal >= config.thresholdCents;
  return byValue || crystalRule(cart);
}

/** The gifts this script manages: the towel, plus the optional crystal bonus gift. */
function gifts() {
  const list = [
    { productId: config.giftProductId, variantId: config.giftVariantId, rejectedKey: REJECTED_KEY, qualifies },
  ];
  if (config.bonusVariantId && config.bonusProductId !== config.giftProductId) {
    list.push({
      productId: config.bonusProductId,
      variantId: config.bonusVariantId,
      rejectedKey: `${REJECTED_KEY}-bonus`,
      qualifies: crystalRule,
    });
  }
  return list;
}

/** Cart state without the gift line - used to remember a rejected (not free) gift. */
function signature(cart) {
  return cart.items
    .filter((item) => !isGiftLine(item))
    .map((item) => `${item.variant_id}:${item.quantity}`)
    .sort()
    .join(',');
}

function sectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);
}

async function getCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/** @param {string} url @param {object} body */
async function post(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: sectionIds().join(',') }),
  });
  return response.json();
}

/** @param {string} key @param {number} quantity */
const changeLine = (key, quantity) => post(`${Theme.routes.cart_change_url}.js`, { id: key, quantity });

function rejectedFor(cart, key = REJECTED_KEY) {
  try {
    return sessionStorage.getItem(key) === signature(cart);
  } catch {
    return false;
  }
}

function rememberRejected(cart, key = REJECTED_KEY) {
  try {
    sessionStorage.setItem(key, signature(cart));
  } catch {
    /* storage unavailable - the bounded loop below still prevents repeats */
  }
}

/** Rejected gift: hide the "wird hinzugefügt …" line - the card keeps only title + rule text. */
function applyRejectedText(cart) {
  const towelLine = cart.items.some((item) => isGiftLine(item) && item.product_id === config.giftProductId);
  if (!rejectedFor(cart) || towelLine || !qualifies(cart)) return;
  document.querySelectorAll('[data-bekra-gift-text]').forEach((element) => {
    if (element instanceof HTMLElement) element.hidden = true;
  });
}

/** Live text of the PDP benefit box (blocks/bekra-gift-hint.liquid). */
function updateHints(cart) {
  const giftFree = cart.items.some(
    (item) => isGiftLine(item) && item.product_id === config.giftProductId && item.final_line_price === 0
  );
  document.querySelectorAll('[data-bekra-gift-hint]').forEach((hint) => {
    if (!(hint instanceof HTMLElement)) return;
    const text = hint.querySelector('[data-bekra-gift-hint-text]');
    if (!text) return;
    const productId = Number(hint.dataset.productId);
    const target = Number(hint.dataset.target) || 3;
    const inCart = cart.items
      .filter((item) => item.product_id === productId)
      .reduce((sum, item) => sum + item.quantity, 0);
    const missing = target - inCart;
    if (giftFree) text.textContent = hint.dataset.securedText || '';
    else if (inCart > 0 && missing > 0)
      text.textContent = `Noch ${missing} ${missing === 1 ? 'weiterer' : 'weitere'} ${hint.dataset.productTitle} bis zum Gratis-Handtuch.`;
    else text.textContent = hint.dataset.defaultText || '';
  });
}

/**
 * @param {any} cart
 * @param {Record<string, string> | undefined} sections
 * @param {boolean} userAction - cart changed by the customer (drawer may open) vs. page load
 */
function notify(cart, sections, userAction) {
  if (userAction) {
    document.dispatchEvent(
      new CartAddEvent({}, SOURCE, { source: SOURCE, itemCount: cart.item_count, sections })
    );
  } else {
    for (const id of sectionIds()) sectionRenderer.renderSection(id, { cache: false });
  }
}

/** @param {boolean} userAction */
async function sync(userAction) {
  if (!config) return;
  if (busy) {
    queued = true;
    return;
  }
  busy = true;

  try {
    let cart = await getCart();
    let lastSections;
    let changed = false;

    // Bounded: at most three corrective steps per gift per sync, never an endless add/remove loop.
    const list = gifts();
    for (let step = 0; step < 3 * list.length; step += 1) {
      let result = null;

      for (const gift of list) {
        const giftLines = cart.items.filter((item) => isGiftLine(item) && item.product_id === gift.productId);
        const shouldHaveGift = gift.qualifies(cart);

        if (giftLines.length > 1) {
          result = await changeLine(giftLines[1].key, 0);
        } else if (giftLines.length === 1 && giftLines[0].quantity > 1) {
          result = await changeLine(giftLines[0].key, 1);
        } else if (giftLines.length === 1 && !shouldHaveGift) {
          result = await changeLine(giftLines[0].key, 0);
        } else if (giftLines.length === 1 && giftLines[0].final_line_price > 0) {
          // Shopify does not discount it (e.g. another non-combinable discount wins): no fake gift.
          rememberRejected(cart, gift.rejectedKey);
          result = await changeLine(giftLines[0].key, 0);
        } else if (giftLines.length === 0 && shouldHaveGift && !rejectedFor(cart, gift.rejectedKey)) {
          result = await post(Theme.routes.cart_add_url, {
            items: [{ id: gift.variantId, quantity: 1, properties: { _bekra_free_gift: 'true' } }],
          });
          if (result && result.status) {
            // add failed (e.g. sold out) - remember this state, do not retry it
            rememberRejected(cart, gift.rejectedKey);
            result = null;
          }
        }
        if (result) break;
      }

      if (!result) break;
      changed = true;
      lastSections = result.sections;
      cart = await getCart();
    }

    if (changed) notify(cart, lastSections, userAction);
    applyRejectedText(cart);
    updateHints(cart);
    // the drawer re-render (section morph) may land after this - apply again afterwards
    const finalCart = cart;
    setTimeout(() => applyRejectedText(finalCart), 600);
  } catch (error) {
    console.error('[bekra-free-gift]', error);
  } finally {
    busy = false;
    if (queued) {
      queued = false;
      sync(true);
    }
  }
}

if (config) {
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.data?.source === SOURCE || detail?.sourceId === SOURCE) return;
    // let Horizon finish its own morph first
    setTimeout(() => sync(true), 50);
  });
  document.addEventListener(ThemeEvents.discountUpdate, () => setTimeout(() => sync(true), 50));
  sync(false);
}
